import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Edit2, Save, X, Check, History, ChevronDown, ChevronUp } from 'lucide-react';
import { chaptersApi } from '../api/chapters';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  chapterId: string;
  chapterNo: number;
}

export default function TranslationPreviewModal({ isOpen, onClose, chapterId, chapterNo }: Props) {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'side-by-side' | 'original' | 'translation'>('side-by-side');
  const [isEditing, setIsEditing] = useState(false);
  const [editedBlocks, setEditedBlocks] = useState<Map<string, string>>(new Map());
  const [showHistory, setShowHistory] = useState(false);
  const [focusedBlockIdx, setFocusedBlockIdx] = useState<number>(-1);
  const [savingStatus, setSavingStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blockRefsMap = useRef<Map<number, HTMLTextAreaElement>>(new Map());

  const { data, isLoading, error } = useQuery({
    queryKey: ['chapter-translation', chapterId],
    queryFn: () => chaptersApi.getTranslation(chapterId),
    enabled: isOpen && !!chapterId,
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['chapter-translation-history', chapterId],
    queryFn: () => chaptersApi.getTranslationHistory(chapterId),
    enabled: isOpen && !!chapterId && showHistory,
  });

  const saveMutation = useMutation({
    mutationFn: (blocks: Array<{ page_no: number; block_id: string; translated_text: string }>) =>
      chaptersApi.updateTranslation(chapterId, blocks),
    onSuccess: () => {
      setSavingStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['chapter-translation', chapterId] });
      queryClient.invalidateQueries({ queryKey: ['chapter-translation-history', chapterId] });
      setTimeout(() => setSavingStatus('idle'), 2000);
    },
    onError: () => {
      setSavingStatus('error');
      setTimeout(() => setSavingStatus('idle'), 3000);
    },
  });

  const blocks = data?.blocks ?? [];
  const untranslatedCount = blocks.filter(b => !b.translation).length;

  const getBlockKey = (block: { page_no: number; block_no: number }) =>
    `${block.page_no}:${block.block_no}`;

  const getEditedValue = (block: { page_no: number; block_no: number; translation: string }) => {
    const key = getBlockKey(block);
    return editedBlocks.has(key) ? editedBlocks.get(key)! : block.translation;
  };

  const isBlockModified = (block: { page_no: number; block_no: number; translation: string }) => {
    const key = getBlockKey(block);
    return editedBlocks.has(key) && editedBlocks.get(key) !== block.translation;
  };

  const handleBlockChange = useCallback((block: { page_no: number; block_no: number }, value: string) => {
    const key = getBlockKey(block);
    setEditedBlocks(prev => {
      const next = new Map(prev);
      next.set(key, value);
      return next;
    });

    // Debounced autosave
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSavingStatus('saving');
    }, 2000);
  }, []);

  // Autosave effect
  useEffect(() => {
    if (savingStatus !== 'saving') return;
    const changedBlocks = blocks
      .filter(b => isBlockModified(b))
      .map(b => ({
        page_no: b.page_no,
        block_id: String(b.block_no),
        translated_text: editedBlocks.get(getBlockKey(b)) || '',
      }));
    if (changedBlocks.length > 0) {
      saveMutation.mutate(changedBlocks);
    } else {
      setSavingStatus('idle');
    }
  }, [savingStatus]);

  const handleSaveAll = useCallback(() => {
    const changedBlocks = blocks
      .filter(b => isBlockModified(b))
      .map(b => ({
        page_no: b.page_no,
        block_id: String(b.block_no),
        translated_text: editedBlocks.get(getBlockKey(b)) || '',
      }));
    if (changedBlocks.length > 0) {
      setSavingStatus('saving');
      saveMutation.mutate(changedBlocks);
    }
  }, [blocks, editedBlocks, saveMutation]);

  const handleSaveAndClose = useCallback(() => {
    const changedBlocks = blocks
      .filter(b => isBlockModified(b))
      .map(b => ({
        page_no: b.page_no,
        block_id: String(b.block_no),
        translated_text: editedBlocks.get(getBlockKey(b)) || '',
      }));
    if (changedBlocks.length > 0) {
      saveMutation.mutate(changedBlocks, {
        onSuccess: () => {
          setIsEditing(false);
          setEditedBlocks(new Map());
          onClose();
        },
      });
    } else {
      setIsEditing(false);
      setEditedBlocks(new Map());
      onClose();
    }
  }, [blocks, editedBlocks, saveMutation, onClose]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedBlocks(new Map());
    setFocusedBlockIdx(-1);
    setSavingStatus('idle');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setViewMode('side-by-side');
  }, []);

  // Keyboard navigation: J/K for block nav, Ctrl+S to save, Esc to cancel
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      // Esc - close modal or cancel edit
      if (e.key === 'Escape') {
        if (isEditing) {
          handleCancelEdit();
        } else {
          onClose();
        }
        e.preventDefault();
        return;
      }

      // Ctrl+S - save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isEditing) handleSaveAll();
        return;
      }

      // J/K navigation only when not typing in textarea
      if (isEditing && (e.target as HTMLElement)?.tagName !== 'TEXTAREA') {
        if (e.key === 'j' || e.key === 'J') {
          e.preventDefault();
          setFocusedBlockIdx(prev => Math.min(prev + 1, blocks.length - 1));
        } else if (e.key === 'k' || e.key === 'K') {
          e.preventDefault();
          setFocusedBlockIdx(prev => Math.max(prev - 1, 0));
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isEditing, blocks.length, handleSaveAll, handleCancelEdit, onClose]);

  // Focus textarea when navigating with J/K
  useEffect(() => {
    if (focusedBlockIdx >= 0 && isEditing) {
      const textarea = blockRefsMap.current.get(focusedBlockIdx);
      if (textarea) {
        textarea.focus();
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [focusedBlockIdx, isEditing]);

  const modifiedCount = blocks.filter(b => isBlockModified(b)).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isEditing ? handleSaveAndClose : onClose}
      title={`Предпросмотр перевода — Глава ${chapterNo}`}
      size="xl"
      footer={isEditing ? (
        <>
          <div className="flex items-center gap-2 mr-auto text-sm">
            {savingStatus === 'saving' && <span className="text-yellow-600 dark:text-yellow-400">Сохранение...</span>}
            {savingStatus === 'saved' && <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Сохранено</span>}
            {savingStatus === 'error' && <span className="text-rose-600 dark:text-rose-400">Ошибка сохранения</span>}
            {modifiedCount > 0 && savingStatus === 'idle' && (
              <span className="text-amber-600 dark:text-amber-400">{modifiedCount} {modifiedCount === 1 ? 'блок изменён' : 'блоков изменено'}</span>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={handleCancelEdit}>
            <X className="h-4 w-4 mr-1" />Отмена
          </Button>
          <Button size="sm" onClick={handleSaveAll} disabled={modifiedCount === 0 || saveMutation.isPending}>
            <Save className="h-4 w-4 mr-1" />{saveMutation.isPending ? 'Сохранение...' : 'Сохранить'}
          </Button>
          <Button size="sm" onClick={handleSaveAndClose} disabled={saveMutation.isPending}>
            <Check className="h-4 w-4 mr-1" />Сохранить и закрыть
          </Button>
        </>
      ) : undefined}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <p className="text-red-500 text-sm">Не удалось загрузить перевод</p>
      ) : blocks.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm py-8 text-center">
          Нет данных перевода для этой главы
        </p>
      ) : (
        <div className="space-y-4">
          {/* Header stats */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span>Блоков: <strong className="text-gray-900 dark:text-white">{data!.total_blocks}</strong></span>
              <span>Переведено: <strong className="text-emerald-600 dark:text-emerald-400">{data!.translated_blocks}</strong></span>
              {untranslatedCount > 0 && (
                <span>Не переведено: <strong className="text-rose-600 dark:text-rose-400">{untranslatedCount}</strong></span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Edit / History buttons */}
              {!isEditing && (
                <>
                  <Button variant="secondary" size="sm" onClick={handleStartEdit}>
                    <Edit2 className="h-3.5 w-3.5 mr-1" />Редактировать
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setShowHistory(!showHistory)}>
                    <History className="h-3.5 w-3.5 mr-1" />История
                    {showHistory ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
                  </Button>
                </>
              )}

              {/* View mode toggle */}
              {!isEditing && (
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  {([
                    { value: 'side-by-side', label: 'Рядом' },
                    { value: 'original', label: 'Оригинал' },
                    { value: 'translation', label: 'Перевод' },
                  ] as const).map(mode => (
                    <button
                      key={mode.value}
                      onClick={() => setViewMode(mode.value)}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        viewMode === mode.value
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Edit mode hint */}
          {isEditing && (
            <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
              <span className="font-medium">Режим редактирования</span> — Ctrl+S сохранить, Esc отменить, J/K навигация по блокам. Автосохранение через 2 сек.
            </div>
          )}

          {/* History panel */}
          {showHistory && !isEditing && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">История изменений</h4>
              {historyLoading ? (
                <LoadingSpinner />
              ) : historyData?.edits?.length ? (
                <div className="space-y-2">
                  {historyData.edits.map((edit) => (
                    <div key={edit.id} className="text-xs border-b border-gray-100 dark:border-gray-700 pb-2">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <span>{edit.edited_by_username || edit.edited_by_email}</span>
                        <span>Стр.{edit.page_no} блок {edit.block_id}</span>
                        <span className="ml-auto">{new Date(edit.created_at).toLocaleString('ru-RU')}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-400">Было:</span>
                          <span className="ml-1 text-rose-600 dark:text-rose-400 line-through">{edit.old_translated_text || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Стало:</span>
                          <span className="ml-1 text-emerald-600 dark:text-emerald-400">{edit.new_translated_text || '—'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Нет истории изменений</p>
              )}
            </div>
          )}

          {/* Blocks */}
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {blocks.map((block, idx) => {
              const isUntranslated = !block.translation && !editedBlocks.has(getBlockKey(block));
              const modified = isBlockModified(block);
              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-3 text-sm transition-colors ${
                    modified
                      ? 'border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/20'
                      : isUntranslated
                        ? 'border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-900/20'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  } ${isEditing && focusedBlockIdx === idx ? 'ring-2 ring-primary-500' : ''}`}
                >
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
                    Стр. {block.page_no}, блок {block.block_no}
                    {isUntranslated && (
                      <span className="ml-2 text-rose-500 font-medium">Не переведено</span>
                    )}
                    {modified && (
                      <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">Изменено</span>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Оригинал</p>
                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap text-sm">{block.original || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Перевод</p>
                        <textarea
                          ref={(el) => {
                            if (el) blockRefsMap.current.set(idx, el);
                            else blockRefsMap.current.delete(idx);
                          }}
                          value={getEditedValue(block)}
                          onChange={(e) => handleBlockChange(block, e.target.value)}
                          onFocus={() => setFocusedBlockIdx(idx)}
                          className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm text-gray-900 dark:text-white resize-y min-h-[2.5rem] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          rows={Math.max(2, (getEditedValue(block) || '').split('\n').length)}
                        />
                      </div>
                    </div>
                  ) : viewMode === 'side-by-side' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Оригинал</p>
                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{block.original || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Перевод</p>
                        <p className={`whitespace-pre-wrap ${
                          isUntranslated ? 'text-rose-400 italic' : 'text-gray-900 dark:text-white'
                        }`}>
                          {block.translation || 'Нет перевода'}
                        </p>
                      </div>
                    </div>
                  ) : viewMode === 'original' ? (
                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{block.original || '—'}</p>
                  ) : (
                    <p className={`whitespace-pre-wrap ${
                      isUntranslated ? 'text-rose-400 italic' : 'text-gray-900 dark:text-white'
                    }`}>
                      {block.translation || 'Нет перевода'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}
